import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Sparklines, SparklinesLine } from "react-sparklines";

// MUI imports
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
  Checkbox,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import { PlayArrow, Stop, ArrowUpward, ArrowDownward } from "@mui/icons-material";

export default function Containers({ token }) {
  const [containers, setContainers] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [history, setHistory] = useState({});
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [selected, setSelected] = useState([]);

  const fetchContainers = async () => {
    try {
      const resp = await axios.get(
        "https://api.harishacrypto.xyz/service18/api/containers",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContainers(resp.data.data);

      for (const c of resp.data.data) {
        const m = await axios.get(
          `https://api.harishacrypto.xyz/service18/api/containers/${c.id}/reports`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const data = m.data.data[0] || {};
        setMetrics((prev) => ({ ...prev, [c.id]: data }));

        setHistory((prev) => ({
          ...prev,
          [c.id]: {
            cpu: [...(prev[c.id]?.cpu || []), data.cpu_percent || 0].slice(-20),
            mem: [
              ...(prev[c.id]?.mem || []),
              ((data.mem_usage_bytes || 0) / 1024 / 1024).toFixed(1),
            ].slice(-20),
          },
        }));
      }
    } catch (err) {
      console.error("Error fetching containers:", err);
    }
  };

  useEffect(() => {
    fetchContainers();
    const interval = setInterval(fetchContainers, 5000);
    return () => clearInterval(interval);
  }, []);

  const control = async (ids, action) => {
    try {
      await Promise.all(
        ids.map((id) =>
          axios.post(
            `https://api.harishacrypto.xyz/service18/api/containers/${id}/${action}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      setSelected([]);
      fetchContainers();
    } catch (err) {
      console.error("Action failed:", err);
    }
  };

  const filteredContainers = containers
    .filter(
      (c) =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.image.toLowerCase().includes(search.toLowerCase()) ||
        (metrics[c.id]?.status || "").toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const ma = metrics[a.id] || {};
      const mb = metrics[b.id] || {};
      let valA, valB;
      switch (sortKey) {
        case "cpu":
          valA = ma.cpu_percent || 0;
          valB = mb.cpu_percent || 0;
          break;
        case "mem":
          valA = (ma.mem_usage_bytes || 0) / 1024 / 1024;
          valB = (mb.mem_usage_bytes || 0) / 1024 / 1024;
          break;
        case "status":
          valA = ma.status || "";
          valB = mb.status || "";
          break;
        default:
          valA = a.name.toLowerCase();
          valB = b.name.toLowerCase();
      }
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

  const toggleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Bulk Actions */}
      {selected.length > 0 && (
        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
          <Chip label={`${selected.length} selected`} color="primary" />
          <Button
            variant="contained"
            color="success"
            onClick={() => control(selected, "start")}
            startIcon={<PlayArrow />}
          >
            Start
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => control(selected, "stop")}
            startIcon={<Stop />}
          >
            Stop
          </Button>
        </Stack>
      )}

      {/* Search & Sort */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={4}
        alignItems="center"
      >
        <TextField
          fullWidth
          variant="outlined"
          label="Search containers..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl>
          <InputLabel>Sort By</InputLabel>
          <Select
            value={sortKey}
            label="Sort By"
            onChange={(e) => setSortKey(e.target.value)}
          >
            <MenuItem value="name">Name</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            <MenuItem value="cpu">CPU</MenuItem>
            <MenuItem value="mem">Memory</MenuItem>
          </Select>
        </FormControl>
        <IconButton
          color="primary"
          onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
        >
          {sortOrder === "asc" ? <ArrowUpward /> : <ArrowDownward />}
        </IconButton>
      </Stack>

      {/* Container Cards */}
      <Grid container spacing={3}>
        {filteredContainers.map((c) => {
          const m = metrics[c.id] || {};
          const h = history[c.id] || { cpu: [], mem: [] };
          const statusColor =
            m.status === "running"
              ? "success"
              : m.status === "stopped"
              ? "error"
              : "default";

          return (
            <Grid item xs={12} sm={6} md={4} key={c.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <Card sx={{ borderRadius: 3, boxShadow: 6 }}>
                  <CardHeader
                    action={
                      <Checkbox
                        checked={selected.includes(c.id)}
                        onChange={() => toggleSelect(c.id)}
                        color="primary"
                      />
                    }
                    title={
                      <Typography variant="h6" fontWeight="bold">
                        {c.name}
                      </Typography>
                    }
                    subheader={<Typography variant="body2">Image: {c.image}</Typography>}
                  />
                  <CardContent>
                    <Chip
                      label={m.status || "unknown"}
                      color={statusColor}
                      size="small"
                      sx={{ mb: 2 }}
                    />

                    {/* CPU Chart */}
                    <Typography variant="body2" gutterBottom>
                      CPU Usage (%)
                    </Typography>
                    <Sparklines data={h.cpu} limit={20} width={100} height={20}>
                      <SparklinesLine color="#1976d2" style={{ strokeWidth: 3, fill: "none" }} />
                    </Sparklines>

                    {/* MEM Chart */}
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      Memory (MB)
                    </Typography>
                    <Sparklines data={h.mem} limit={20} width={100} height={20}>
                      <SparklinesLine color="#d81b60" style={{ strokeWidth: 3, fill: "none" }} />
                    </Sparklines>

                    <Divider sx={{ my: 2 }} />

                    {/* Stats */}
                    <Stack direction="row" justifyContent="space-between" mb={2}>
                      <Typography variant="body2">
                        CPU: {m.cpu_percent?.toFixed(1) || 0}%
                      </Typography>
                      <Typography variant="body2">
                        Mem: {((m.mem_usage_bytes || 0) / 1024 / 1024).toFixed(1)} MB
                      </Typography>
                    </Stack>

                    {/* Actions */}
                    <Stack direction="row" spacing={2}>
                      {m.status !== "running" && (
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          onClick={() => control([c.id], "start")}
                        >
                          Start
                        </Button>
                      )}
                      {m.status === "running" && (
                        <Button
                          variant="contained"
                          color="warning"
                          fullWidth
                          onClick={() => control([c.id], "stop")}
                        >
                          Stop
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
